import type { ClientDTO, Language, RelationType } from '@brainkey/shared';
import { X } from 'lucide-react';

import { fullName } from '../lib/format';

import { RelatedClientPicker } from './RelatedClientPicker';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { PhoneNumberField } from './ui/PhoneNumberField';
import { Select } from './ui/Select';

/** One relation-contact row's editable fields — shared by CreateClientModal's
 * "Family & Relations" step and ClientDetailPage's ContactsSection, the only
 * two places this field group is needed (DEC-0042). */
export interface ContactFormValue {
  relationType: RelationType;
  name: string;
  phone: string;
  email: string;
  languagePref: Language;
  /** Set via RelatedClientPicker — links this row to a real Client instead
   * of free text. When set, name/phone/email/languagePref are live-synced
   * from that client server-side, not a stale snapshot. */
  relatedClientId: string | null;
}

export const EMPTY_CONTACT: ContactFormValue = {
  relationType: 'MOTHER',
  name: '',
  phone: '',
  email: '',
  languagePref: 'EN',
  relatedClientId: null,
};

const RELATION_OPTIONS = [
  { value: 'MOTHER', label: 'Mother' },
  { value: 'FATHER', label: 'Father' },
  { value: 'GUARDIAN', label: 'Guardian' },
  { value: 'SIBLING', label: 'Sibling' },
  { value: 'SPOUSE', label: 'Spouse' },
  { value: 'OTHER', label: 'Other' },
];

const LANGUAGE_OPTIONS = [
  { value: 'EN', label: 'English' },
  { value: 'SI', label: 'Sinhala' },
  { value: 'TA', label: 'Tamil' },
];

/** True once a row has enough to submit: linked to a client, or a name and
 * at least phone or email. */
export function isContactRowFilled(v: ContactFormValue): boolean {
  if (v.relatedClientId !== null) return v.name.trim() !== '';
  return v.name.trim() !== '' && (v.phone.trim() !== '' || v.email.trim() !== '');
}

export function ClientContactFields({
  value,
  onChange,
  onRemove,
  /** The client whose relations are being edited — excluded from the link
   * picker's search results so a client can't be linked to themselves.
   * Omitted while creating a brand-new client (no id exists yet). */
  excludeClientId,
}: {
  value: ContactFormValue;
  onChange: (next: ContactFormValue) => void;
  onRemove?: () => void;
  excludeClientId?: string;
}): JSX.Element {
  const set = <K extends keyof ContactFormValue>(key: K, v: ContactFormValue[K]): void => {
    onChange({ ...value, [key]: v });
  };

  const linked = value.relatedClientId !== null;

  const handleLink = (client: ClientDTO): void => {
    onChange({
      ...value,
      relatedClientId: client.id,
      name: fullName(client),
      phone: client.phone ?? '',
      email: client.email ?? '',
      languagePref: client.languagePref,
    });
  };

  const handleUnlink = (): void => {
    set('relatedClientId', null);
  };

  return (
    <div className="relative flex flex-col gap-4 p-4 rounded-2xl bg-zinc-50 border border-zinc-100 dark:bg-zinc-900/40 dark:border-white/5">
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove contact"
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full text-zinc-400 hover:text-rose-600 hover:bg-rose-50 dark:text-zinc-500 dark:hover:text-rose-400 dark:hover:bg-rose-500/10 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      <div className="pr-8">
        <RelatedClientPicker
          relatedClientId={value.relatedClientId}
          linkedName={value.name}
          excludeClientId={excludeClientId}
          onLink={handleLink}
          onUnlink={handleUnlink}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pr-8">
        <Select
          label="Relation"
          value={value.relationType}
          onValueChange={(v) => set('relationType', v as RelationType)}
          options={RELATION_OPTIONS}
        />
        <Input
          label="Name"
          value={value.name}
          onChange={(e) => set('name', e.target.value)}
          disabled={linked}
        />
        <PhoneNumberField
          label="Phone"
          optional
          value={value.phone}
          onChange={(v) => set('phone', v)}
          disabled={linked}
        />
        <Input
          label="Email"
          type="email"
          optional
          value={value.email}
          onChange={(e) => set('email', e.target.value)}
          disabled={linked}
        />
        <Select
          label="Preferred language"
          value={value.languagePref}
          onValueChange={(v) => set('languagePref', v as Language)}
          options={LANGUAGE_OPTIONS}
          disabled={linked}
        />
      </div>
    </div>
  );
}

export function AddContactButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <Button variant="ghost" type="button" onClick={onClick}>
      + Add another contact
    </Button>
  );
}
